<template>
  <div v-if="isPicture" class="agent-attachment agent-attachment--image">
    <button type="button" class="agent-attachment__open" :title="`Open ${fileName}`" @click="imageModalRef?.open()">
      <img class="agent-attachment__preview" :src="imagePath(latest!.path, 480, 360, 'fit')" :alt="fileName" />
    </button>

    <a
      class="agent-attachment__caption"
      :href="downloadUrl"
      :title="`Download ${fileName}`"
      target="_blank"
      rel="noopener"
    >
      <span class="agent-attachment__name">{{ fileName }}</span>
      <span v-if="size" class="agent-attachment__size">{{ formatBytes(size) }}</span>
    </a>

    <fs-modal ref="imageModalRef">
      <div class="agent-attachment__modal">
        <annotation-editor v-model="viewerImage" :measurements="[]" readonly full-resolution />
      </div>
    </fs-modal>
  </div>

  <!-- A spreadsheet or a PDF gets a thumbnail of its own rendering, which is worth showing —
       but there is nothing to zoom into, so the whole card downloads. -->
  <a
    v-else-if="previewPath"
    class="agent-attachment agent-attachment--image"
    :href="downloadUrl"
    :title="`Download ${fileName}`"
    target="_blank"
    rel="noopener"
  >
    <img class="agent-attachment__preview" :src="imagePath(previewPath, 480, 360, 'fit')" :alt="fileName" />
    <span class="agent-attachment__caption">
      <span class="agent-attachment__name">{{ fileName }}</span>
      <span v-if="size" class="agent-attachment__size">{{ formatBytes(size) }}</span>
    </span>
  </a>

  <a v-else class="agent-attachment" :href="downloadUrl" :title="`Download ${fileName}`" target="_blank" rel="noopener">
    <file-icon :file-name="fileName" class="agent-attachment__icon" />
    <span class="agent-attachment__name">{{ fileName }}</span>
    <span v-if="size" class="agent-attachment__size">{{ formatBytes(size) }}</span>
  </a>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { AgentAttachmentDto, MChartImageDto } from '@/ApiClient';
import { useAzureBlobFiles } from '@/store/azure-blob-files';
import { formatBytes, isImage } from '@lib/files';
import { imagePath } from '@lib/imgproxy';
import FileIcon from '@components/files/file-icon.vue';
import FsModal from '@components/modals/fs-modal.vue';
import AnnotationEditor from '@pages/product/product-details-subsections/mchart-subsection/annotation-editor.vue';

const { attachment } = defineProps<{ attachment: AgentAttachmentDto }>();

const latest = computed(() => attachment.file.versions[0]);
const fileName = computed(() =>
  latest.value ? `${attachment.file.name}.${latest.value.extension}` : attachment.file.name
);
const size = computed(() => latest.value?.size ?? 0);
const downloadUrl = computed(() => useAzureBlobFiles().downloadLatestUrl(attachment.file.id));

// Only a real image opens in the viewer. A spreadsheet's thumbnail is a picture of a PDF
// rendering of it, and the file behind it is not something an image viewer can show — pointing
// the viewer at the .xlsx is what made clicking one a broken image.
const isPicture = computed(() => !!latest.value && isImage(latest.value.extension));
const previewPath = computed(() => latest.value?.thumbnailPath ?? null);

const imageModalRef = ref<InstanceType<typeof FsModal> | null>(null);

// The editor a sample round's messages open an image in, readonly — it is what carries the zoom.
// It wants an mchart image, which an attachment is not: there are no annotations, and the
// dimensions are only there for a file that had them recorded.
const viewerImage = computed<MChartImageDto>(() => ({
  id: attachment.file.id,
  name: fileName.value,
  path: latest.value?.path ?? '',
  size: size.value,
  width: latest.value?.width ?? 0,
  height: latest.value?.height ?? 0,
  annotations: [],
}));
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

.agent-attachment {
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  max-width: 100%;
  padding: 0.6rem 1rem 0.6rem 0.8rem;
  border: 1px solid $c-hairline-strong;
  border-radius: $br-medium-heavy;
  background: $c-surface;
  color: $c-ink;
  font-size: 1.3rem;
  text-decoration: none;
  transition: background $transition-fast ease;

  &:hover {
    background: $c-surface-muted;
  }

  &__icon {
    flex: none;
  }

  &__name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__size {
    flex: none;
    color: $c-ink-soft;
    font-size: 1.2rem;
  }

  // ── Image preview ────────────────────────────────────────────────────────
  &--image {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    max-width: 32rem;
    padding: 0;
    overflow: hidden;

    &:hover {
      background: $c-surface;
    }
  }

  &__open {
    display: block;
    padding: 0;
    border: none;
    background: none;
    cursor: zoom-in;
  }

  &__preview {
    display: block;
    width: 100%;
    max-height: 24rem;
    object-fit: contain;
    background: $c-surface-muted;
  }

  &__caption {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    min-width: 0;
    padding: 0.6rem 1rem;
    border-top: 1px solid $c-hairline;
    color: $c-ink;
    text-decoration: none;

    &:hover {
      background: $c-surface-muted;
      text-decoration: underline;
    }
  }

  &__modal {
    width: 90vw;
    height: 90vh;
  }
}
</style>
